// src/components/purchase/SettleNowModal.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBalanceScale, faCheckCircle, faSpinner, faBoxOpen, faWallet } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { usePopup } from '../context/PopupContext';
import api from '../../api';
import useDialog from '../common/ui/useDialog';
import { formatPurchasingCurrency as formatCurrency } from '../../utils/formatters';
import './SettleNowModal.css';

const SettleNowModal = ({ isOpen, onClose, invoice, supplierId, onSuccess }) => {
  const { showPopup } = usePopup();

  const [credits, setCredits] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef(null);
  // Escape closes (not while saving); focus moves in; Tab and page scroll stay put.
  useDialog(dialogRef, { open: Boolean(isOpen && invoice), onClose: submitting ? undefined : onClose });

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/suppliers/${supplierId}/credits`);
      const combined = [
        ...(data.overpayments || []),
        ...(data.return_credits || [])
      ].sort((a, b) => new Date(a.credit_date) - new Date(b.credit_date));
      setCredits(combined);
      setAllocations({});
    } catch (err) {
      console.error(err);
      showPopup('error', 'Failed to load supplier credits');
    } finally {
      setLoading(false);
    }
  }, [showPopup, supplierId]);

  useEffect(() => {
    if (isOpen && supplierId) fetchCredits();
  }, [fetchCredits, isOpen, supplierId]);

  if (!isOpen || !invoice) return null;

  const totalAllocated = Object.values(allocations).reduce((sum, amt) => sum + (parseFloat(amt) || 0), 0);
  const remainingInvoiceBalance = parseFloat(invoice.balance_amount) - totalAllocated;

  const handleAllocationChange = (credit, value) => {
    let amt = parseFloat(value);
    if (isNaN(amt)) amt = 0;

    const maxForCredit = parseFloat(credit.remaining_credit);
    if (amt > maxForCredit) amt = maxForCredit;

    const currentAlloc = parseFloat(allocations[credit.id]) || 0;
    const maxForInvoice = parseFloat(invoice.balance_amount) - (totalAllocated - currentAlloc);
    if (amt > maxForInvoice) amt = maxForInvoice;

    setAllocations(prev => ({ ...prev, [credit.id]: amt }));
  };

  const handleSetMax = (credit) => {
    const currentAlloc = parseFloat(allocations[credit.id]) || 0;
    const maxPossible = Math.min(
      parseFloat(credit.remaining_credit),
      parseFloat(invoice.balance_amount) - (totalAllocated - currentAlloc)
    );
    setAllocations(prev => ({ ...prev, [credit.id]: maxPossible }));
  };

  const handleSubmit = async () => {
    const creditsToApply = credits
      .filter(c => parseFloat(allocations[c.id]) > 0)
      .map(c => ({
        credit_type: c.credit_type,
        credit_id: c.id,
        amount: parseFloat(allocations[c.id])
      }));

    if (creditsToApply.length === 0) return showPopup('error', 'Please allocate at least one credit');

    if (invoice?.supplier_id) {
      const mismatch = credits
        .filter(c => parseFloat(allocations[c.id]) > 0)
        .find(c => c.supplier_id && String(c.supplier_id) !== String(invoice.supplier_id));
      if (mismatch) {
        return showPopup(
          'error',
          `Cannot use ${mismatch.credit_number} to settle this invoice — ` +
          'the credit belongs to a different supplier.'
        );
      }
    }

    setSubmitting(true);
    try {
      const response = await api.post('/suppliers/settle-credit', {
        purchase_id: invoice.id,
        settlement_date: dayjs().format('YYYY-MM-DD'),
        notes: `Settled from available supplier credits`,
        credits: creditsToApply
      });

      showPopup('success', response.data.message || 'Credit settled successfully!', {
        title: 'Settlement Successful',
        confirmText: 'OK'
      });
      
      // ✅ Fix: Call onSuccess with the response data
      if (onSuccess) {
        await onSuccess(response.data);
      }
      
      onClose();
    } catch (err) {
      console.error(err);
      showPopup('error', err.response?.data?.error || 'Failed to settle credit');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal((
    // Backdrop is inert: an outside click must not discard what was typed.
    <div className="snm-overlay">
      <div ref={dialogRef} className="snm-modal" role="dialog" aria-modal="true" aria-label="Settle Now">
        
        <div className="snm-header">
          <h3>
            <FontAwesomeIcon icon={faBalanceScale} />
            Settle Invoice with Credit
          </h3>
          <button className="snm-close" onClick={onClose} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="snm-body">
          <div className="snm-summary">
            <div className="snm-summary-left">
              <h4>Invoice {invoice.invoice_no}</h4>
              <p>{dayjs(invoice.purchase_date).format('DD MMM YYYY')}</p>
            </div>
            <div className="snm-summary-right">
              <span>Current Balance</span>
              <strong>{formatCurrency(invoice.balance_amount)}</strong>
            </div>
          </div>

          <div>
            <h4 className="snm-section-title">
              <FontAwesomeIcon icon={faWallet} /> Available Credits
            </h4>
            
            {loading ? (
              <div className="snm-empty"><FontAwesomeIcon icon={faSpinner} spin /> Loading credits...</div>
            ) : credits.length === 0 ? (
              <div className="snm-empty">
                <FontAwesomeIcon icon={faBoxOpen} />
                <p>No available credits or overpayments found for this supplier.</p>
              </div>
            ) : (
              <div className="snm-credit-list">
                {credits.map(credit => {
                  const amt = allocations[credit.id] || '';
                  const maxAllowed = Math.min(parseFloat(credit.remaining_credit), remainingInvoiceBalance + (parseFloat(amt)||0));
                  const isSelected = parseFloat(amt) > 0;

                  return (
                    <div key={credit.id} className={`snm-credit-item ${isSelected ? 'selected' : ''}`}>
                      <div className="snm-ci-info">
                        <div className="snm-ci-header">
                          <span className={`snm-ci-type ${credit.credit_type === 'return_credit' ? 'return' : 'advance'}`}>
                            {credit.source}
                          </span>
                          <span className="snm-ci-num">{credit.credit_number}</span>
                          <span className="snm-ci-date">{dayjs(credit.credit_date).format('DD MMM YYYY')}</span>
                        </div>
                        <div className="snm-ci-details">
                          <span className="sn-avail-amt">Original: {formatCurrency(credit.original_amount)}</span>
                          <strong>Available: {formatCurrency(credit.remaining_credit)}</strong>
                        </div>
                      </div>
                      
                      <div className="snm-ci-action">
                        <div className="snm-ci-input-group">
                          <input
                            type="number"
                            min="0"
                            step="0.0001"
                            placeholder="0.00"
                            value={amt}
                            onChange={(e) => handleAllocationChange(credit, e.target.value)}
                            disabled={maxAllowed <= 0 && !amt}
                          />
                          <button
                            type="button"
                            className="snm-ci-max"
                            onClick={() => handleSetMax(credit)}
                            disabled={maxAllowed <= 0 && !amt}
                          >
                            Max
                          </button>
                        </div>
                        <FontAwesomeIcon icon={faCheckCircle} className="snm-ci-check" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="snm-totals">
          <div className="snm-total-item">
            <span>Invoice Balance</span>
            <span>{formatCurrency(invoice.balance_amount)}</span>
          </div>
          <div className="snm-total-item active">
            <span>Total Settled</span>
            <span>Rs. {totalAllocated.toFixed(4)}</span>
          </div>
          <div className={`snm-total-item rem ${remainingInvoiceBalance <= 0 ? 'zero' : ''}`}>
            <span>Remaining Balance</span>
            <span>Rs. {Math.max(0, remainingInvoiceBalance).toFixed(4)}</span>
          </div>
        </div>

        <div className="snm-footer">
          <button className="snm-btn-cancel" onClick={onClose} type="button">Cancel</button>
          <button
            className="snm-btn-submit"
            type="button"
            onClick={handleSubmit}
            disabled={submitting || totalAllocated <= 0}
          >
            {submitting 
              ? <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</> 
              : <><FontAwesomeIcon icon={faCheckCircle} /> Apply Credit</>}
          </button>
        </div>

      </div>
    </div>
  ), document.getElementById('modal-root') || document.body);
};

export default SettleNowModal;